import React, { useState, useEffect } from 'react';
import { FaTimes, FaFilter, FaUser, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { db } from '../firebase';
import { collection, query, orderBy, limit, getDocs, startAfter } from 'firebase/firestore';
import './UserSettings.css';
import TakeoverDetail from './TakeoverDetail';
import CampaignDetail from './CampaignDetail';
import CreativesList from './CreativesList';

const PAGE_SIZE = 50;

const ActivityLogModal = ({ open, onClose, userName }) => {
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState('all');
  const [userMap, setUserMap] = useState({});
  const [page, setPage] = useState(1);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [detailModal, setDetailModal] = useState({ open: false, type: null, data: null });
  const [logType, setLogType] = useState('admin'); // 'admin' or 'user'

  useEffect(() => {
    if (open) {
      fetchUsers();
      if (logType === 'admin') {
        fetchActivityLogs(1, null);
      } else {
        fetchUserActivityLogs();
      }
    }
    // eslint-disable-next-line
  }, [open, logType]);

  useEffect(() => {
    if (open && logType === 'admin') fetchActivityLogs(page, lastDoc);
    // eslint-disable-next-line
  }, [filterAction, filterUser, page]);

  // Fetch all admin users for dropdown and for name lookup
  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const usersList = usersSnapshot.docs
        .map(doc => ({ uid: doc.id, ...doc.data() }))
        .filter(user => user.admin === true);
      setUsers(usersList);
      // Build a map for quick lookup
      const map = {};
      usersList.forEach(u => { map[u.uid] = u; });
      setUserMap(map);
    } catch (error) {
      setUsers([]);
      setUserMap({});
    }
  };

  // Fetch logs with pagination and filtering
  const fetchActivityLogs = async (pageNum = 1, lastDocRef = null) => {
    setLoading(true);
    try {
      let logsRef = collection(db, 'admin_activity_logs');
      let q = query(logsRef, orderBy('timestamp', 'desc'), limit(PAGE_SIZE + 1));
      if (lastDocRef && pageNum > 1) {
        q = query(logsRef, orderBy('timestamp', 'desc'), startAfter(lastDocRef), limit(PAGE_SIZE + 1));
      }
      const querySnapshot = await getDocs(q);
      let logs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));
      // Filtering
      if (filterAction !== 'all') logs = logs.filter(log => log.action === filterAction);
      if (filterUser !== 'all') logs = logs.filter(log => log.userId === filterUser);
      // Pagination logic
      setHasNextPage(logs.length > PAGE_SIZE);
      if (logs.length > PAGE_SIZE) logs = logs.slice(0, PAGE_SIZE);
      setActivityLogs(logs);
      setLastDoc(querySnapshot.docs[logs.length - 1] || null);
    } catch (error) {
      setActivityLogs([]);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user activity logs
  const fetchUserActivityLogs = async () => {
    setLoading(true);
    try {
      const userQ = query(collection(db, 'user_activity'));
      const userSnap = await getDocs(userQ);
      const logs = userSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          action: data.action || data.details?.action,
          description: data.details?.page ? `Page: ${data.details.page}` : '',
          userEmail: data.details?.userId,
          timestamp: data.timestamp || data.details?.timestamp,
          page: data.page || data.details?.page,
          signId: data.signId || data.details?.signId,
        };
      });
      setActivityLogs(logs);
      setHasNextPage(false);
    } catch (error) {
      setActivityLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login': return '🔑';
      case 'creative_approval': return '✅';
      case 'creative_rejection': return '❌';
      case 'campaign_approval': return '📢';
      case 'campaign_rejection': return '🚫';
      case 'takeover_approval': return '🎯';
      case 'takeover_rejection': return '⛔';
      case 'user_update': return '👤';
      case 'file_upload': return '📁';
      default: return '📝';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
      // Firestore Timestamp object
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleString();
      }
      // ISO string or string
      if (typeof timestamp === 'string') {
        const dateObj = new Date(timestamp);
        if (!isNaN(dateObj.getTime())) return dateObj.toLocaleString();
      }
      // JS Date
      if (timestamp instanceof Date) {
        return timestamp.toLocaleString();
      }
      // Fallback: try to parse
      const dateObj = new Date(timestamp);
      if (!isNaN(dateObj.getTime())) return dateObj.toLocaleString();
      return 'Unknown';
    } catch {
      return 'Unknown';
    }
  };

  // Skeleton loader
  const renderSkeleton = () => (
    <div className="logs-list" style={{overflowX:'hidden'}}>
      {[...Array(PAGE_SIZE)].map((_, i) => (
        <div key={i} className="log-item skeleton" style={{height:64,marginBottom:8,background:'#f3f4f6',borderRadius:8,display:'flex',alignItems:'center',padding:'0 24px',maxWidth:'100%',overflow:'hidden'}}>
          <div style={{width:32,height:32,background:'#e5e7eb',borderRadius:'50%',marginRight:16}}></div>
          <div style={{flex:1}}>
            <div style={{height:16,width:'40%',background:'#e5e7eb',marginBottom:8,borderRadius:4}}></div>
            <div style={{height:12,width:'30%',background:'#e5e7eb',borderRadius:4}}></div>
          </div>
        </div>
      ))}
    </div>
  );

  // Click handler for log items
  const handleLogClick = async (log) => {
    if (log.creativeId) {
      // Fetch creative data and show in modal
      const creativeDoc = await getDocs(query(collection(db, 'creatives')));
      const creative = creativeDoc.docs.map(d => ({ id: d.id, ...d.data() })).find(c => c.id === log.creativeId);
      if (creative) setDetailModal({ open: true, type: 'creative', data: creative });
    } else if (log.takeoverId) {
      // Fetch takeover data and show in modal
      const takeoverDoc = await getDocs(query(collection(db, 'orders')));
      const takeover = takeoverDoc.docs.map(d => ({ id: d.id, ...d.data() })).find(t => t.id === log.takeoverId);
      if (takeover) setDetailModal({ open: true, type: 'takeover', data: takeover });
    } else if (log.campaignId) {
      // Fetch campaign data and show in modal
      const campaignDoc = await getDocs(query(collection(db, 'campaigns')));
      const campaign = campaignDoc.docs.map(d => ({ id: d.id, ...d.data() })).find(c => c.id === log.campaignId);
      if (campaign) setDetailModal({ open: true, type: 'campaign', data: campaign });
    }
  };

  // Compose log description with user name
  const getLogDescription = (log) => {
    const user = userMap[log.userId];
    const displayName = user ? (user.name || user.displayName || user.email || log.userId) : log.userEmail || log.userId;
    // Replace 'User logged in' with 'Brian Becker logged in'
    if (log.action === 'login') {
      return `${displayName} logged in`;
    }
    // For other actions, prepend user name
    return `${displayName}: ${log.description}`;
  };

  if (!open) return null;

  return (
    <div className="settings-modal-overlay" style={{zIndex: 2000,overflowX:'hidden'}}>
      <div className="settings-modal" style={{maxWidth:'100vw',overflowX:'hidden'}}>
        <div className="settings-header" style={{overflowX:'hidden'}}>
          <h2>Activity Logs</h2>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <button
              onClick={() => setLogType('admin')}
              style={{ fontWeight: logType === 'admin' ? 'bold' : 'normal', padding: '6px 18px', borderRadius: 8, border: logType === 'admin' ? '2px solid #3b82f6' : '1px solid #e5e7eb', background: logType === 'admin' ? '#e0e7ff' : '#f8fafc', cursor: 'pointer' }}
            >
              Admin Activity
            </button>
            <button
              onClick={() => setLogType('user')}
              style={{ fontWeight: logType === 'user' ? 'bold' : 'normal', padding: '6px 18px', borderRadius: 8, border: logType === 'user' ? '2px solid #3b82f6' : '1px solid #e5e7eb', background: logType === 'user' ? '#e0e7ff' : '#f8fafc', cursor: 'pointer' }}
            >
              User Activity
            </button>
          </div>
          <div style={{marginBottom: 12, fontWeight: 600, color: logType === 'admin' ? '#2563eb' : '#059669', fontSize: 18}}>
            {logType === 'admin' ? 'Admin Activity Logs' : 'User Activity Logs'}
          </div>
          {logType === 'admin' && (
            <div className="header-actions">
              <div className="filter-container">
                <FaFilter />
                <select 
                  value={filterAction} 
                  onChange={(e) => { setFilterAction(e.target.value); setPage(1); }}
                  className="action-filter"
                >
                  <option value="all">All Actions</option>
                  <option value="login">Logins</option>
                  <option value="creative_approval">Creative Approvals</option>
                  <option value="creative_rejection">Creative Rejections</option>
                  <option value="campaign_approval">Campaign Approvals</option>
                  <option value="campaign_rejection">Campaign Rejections</option>
                  <option value="takeover_approval">Takeover Approvals</option>
                  <option value="takeover_rejection">Takeover Rejections</option>
                  <option value="user_update">User Updates</option>
                  <option value="file_upload">File Uploads</option>
                </select>
              </div>
              <div className="filter-container">
                <FaUser />
                <select
                  value={filterUser}
                  onChange={e => { setFilterUser(e.target.value); setPage(1); }}
                  className="action-filter"
                >
                  <option value="all">All Admins</option>
                  {users.map(user => (
                    <option key={user.uid} value={user.uid}>
                      {user.name || user.displayName || user.email || user.uid}
                    </option>
                  ))}
                </select>
              </div>
              <button className="refresh-btn" onClick={() => fetchActivityLogs(page, lastDoc)}>
                Refresh
              </button>
              <button className="close-btn" onClick={onClose}>
                <FaTimes />
              </button>
            </div>
          )}
        </div>
        <div className="settings-content" style={{overflowX:'hidden'}}>
          <div className="user-info" style={{background: logType === 'admin' ? '#e0e7ff' : '#d1fae5'}}>
            <h3>{userName}</h3>
          </div>
          <div className="activity-logs" style={{overflowX:'hidden'}}>
            {loading ? (
              renderSkeleton()
            ) : activityLogs.length > 0 ? (
              <div className="logs-list" style={{overflowX:'hidden'}}>
                {activityLogs.map(log => (
                  <div key={log.id} className="log-item" style={{cursor:'pointer',maxWidth:'100%',overflow:'hidden',background: logType === 'admin' ? '#f8fafc' : '#f0fdf4', borderLeft: logType === 'admin' ? '4px solid #2563eb' : '4px solid #059669'}} onClick={() => handleLogClick(log)}>
                    <div className="log-icon">{getActionIcon(log.action)}</div>
                    <div className="log-content">
                      <div className="log-header">
                        <div className="log-action">
                          {logType === 'admin' ? getLogDescription(log) : (
                            <>
                              <b>Action:</b> {log.action || 'Unknown'}
                              {log.page && <span style={{marginLeft:8}}><b>Page:</b> {log.page}</span>}
                            </>
                          )}
                        </div>
                        <div className="log-time">{formatTimestamp(log.timestamp)}</div>
                      </div>
                      {logType === 'user' && log.signId &&
                        log.action === 'sign_view' && ['sign_detail', 'search'].includes(log.page) && (
                        <div className="log-details"><b>Sign ID:</b> {log.signId}</div>
                      )}
                      {logType === 'user' && log.userEmail && (
                        <div className="log-details"><b>User ID:</b> {log.userEmail}</div>
                      )}
                      {logType === 'admin' && log.reason && (
                        <div className="log-reason">Reason: {log.reason}</div>
                      )}
                      <div className="log-details">
                        {logType === 'admin' && <span className="log-user">{log.userEmail}</span>}
                        {logType === 'admin' && <span className="log-id">UID: {log.userId}</span>}
                        {log.deviceType && log.action === 'login' && (
                          <span className="log-id">Device: {log.deviceType}</span>
                        )}
                        {log.creativeId && <span className="log-id">Creative ID: {log.creativeId}</span>}
                        {log.campaignId && <span className="log-id">Campaign ID: {log.campaignId}</span>}
                        {log.takeoverId && <span className="log-id">Takeover ID: {log.takeoverId}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-logs">No activity logs found</div>
            )}
            {/* Pagination Controls */}
            {logType === 'admin' && (
              <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:16,marginTop:24}}>
                <button
                  className="refresh-btn"
                  style={{padding:'8px 18px',fontSize:18,background:'#f1f5f9',color:'#64748b',border:'none',borderRadius:8,cursor:page===1?'not-allowed':'pointer',opacity:page===1?0.5:1}}
                  onClick={() => page > 1 && setPage(page-1)}
                  disabled={page===1}
                >
                  <FaChevronLeft /> Prev
                </button>
                <span style={{fontWeight:600,fontSize:16}}>Page {page}</span>
                <button
                  className="refresh-btn"
                  style={{padding:'8px 18px',fontSize:18,background:'#f1f5f9',color:'#64748b',border:'none',borderRadius:8,cursor:!hasNextPage?'not-allowed':'pointer',opacity:!hasNextPage?0.5:1}}
                  onClick={() => hasNextPage && setPage(page+1)}
                  disabled={!hasNextPage}
                >
                  Next <FaChevronRight />
                </button>
              </div>
            )}
          </div>
        </div>
        {/* Detail Modal */}
        {detailModal.open && detailModal.type !== 'campaign' && (
          <div className="settings-modal-overlay" style={{zIndex:2100,background:'rgba(30,41,59,0.45)',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <div className="settings-modal" style={{maxWidth:900,overflowY:'auto',maxHeight:'90vh',padding:0}}>
              <button className="close-btn" style={{position:'absolute',top:18,right:18,fontSize:28,background:'none',border:'none',color:'#64748b',cursor:'pointer',zIndex:2200}} onClick={()=>setDetailModal({open:false,type:null,data:null})}>&times;</button>
              {detailModal.type === 'creative' && (
                <div style={{padding:32}}>
                  <h3 style={{marginBottom:18}}>Creative Details</h3>
                  {/* Render creative like CreativesList.js */}
                  <div style={{display:'flex',gap:24}}>
                    <img src={detailModal.data.croppedImageUrl || detailModal.data.originalImageUrl} alt="Creative" style={{width:300,borderRadius:12,boxShadow:'0 2px 8px rgba(30,41,59,0.08)'}} />
                    <div style={{flex:1}}>
                      <div><b>ID:</b> {detailModal.data.id}</div>
                      <div><b>Status:</b> {detailModal.data.moderationStatus}</div>
                      <div><b>Content Score:</b> {detailModal.data.contentScore}</div>
                      <div><b>Created:</b> {formatTimestamp(detailModal.data.createdAt)}</div>
                      <div><b>Sign ID:</b> {detailModal.data.signId}</div>
                      <div><b>User ID:</b> {detailModal.data.userId}</div>
                      <div><b>Rejection Reason:</b> {detailModal.data.rejectionReason}</div>
                      <div><b>Content Flags:</b> {detailModal.data.contentFlags && detailModal.data.contentFlags.join(', ')}</div>
                    </div>
                  </div>
                </div>
              )}
              {detailModal.type === 'takeover' && (
                <TakeoverDetail takeover={detailModal.data} refreshTakeover={()=>{}} />
              )}
            </div>
          </div>
        )}
        
        {/* Campaign Detail renders its own modal */}
        {detailModal.open && detailModal.type === 'campaign' && (
          <CampaignDetail campaign={detailModal.data} onClose={() => setDetailModal({ open: false, type: null, data: null })} />
        )}
      </div>
    </div>
  );
};

export default ActivityLogModal; 